// @givmo/app-charity — Claim wizard (CPA-WEB2, plan A-1).
//
// The selected org's claim flow: load the charity → the CEREMONY step (the Charity Terms, the signer's
// role and title, and two affirmations — see the bundled ceremony below) → submit → branch on the
// response `outcome`. Wiring (unchanged):
//   - LOAD    → `charitiesGetCharity` (GET /charities/{id}) via `charitiesGetCharityOptions`.
//   - SUBMIT  → `charityAdminSubmitCharityClaim` (POST /charity-admin/charities/{id}/claim) via the
//               `charityAdminSubmitCharityClaimMutation` factory. Body: `{ attested: true,
//               attested_legal_name, attested_ein }` plus the acceptance's four fields (below). The
//               response is `CharityClaimSubmitResultOut` and returns 200 in ALL cases — `outcome`
//               discriminates the terminal shape:
//                 • AUTO_VERIFIED → instant-success screen (the claimant is now the Owner).
//                 • PENDING_REVIEW → Tier-2: route to the claim-detail page (document upload +
//                   pending-status). This ALSO covers a takeover of an already-claimed org (a claim
//                   on a claimed org is ALWAYS PENDING_REVIEW, even on a domain match — plan §3.5).
//                 • CANNOT_CLAIM → a deactivated/screened dead-end: `request` is null, `message`
//                   carries the plain-language contact-support copy. A clean dead-end, NOT an error.
//
// ★ THE ATTESTATION IS THE CEREMONY (CPA-DP1, 2026-08-10). This is the one legally-operative act in the
// funnel — a person stating, on the record, that they may bind a nonprofit — and the screen used to rank
// it below its own footnote: a tinted info band led, the attestation sat in a plain hairline box under
// it, and the organization being claimed was a 40px avatar in a card header. The rebuild inverts that:
//   - the ORGANIZATION is the figure (name at heading scale, EIN and website beneath it);
//   - the ATTESTATION is the loudest block on the screen, not the quietest (today the signing block's
//     two affirmations, `claim-signing-block.tsx`);
//   - the fork is disclosed PERSONALLY and BEFORE the commitment (see `claim-expectation.ts`), naming
//     the signed-in address and the website on record instead of asking the reader to compare them —
//     and never promising instant verification without its condition;
//   - there is an exit for the wrong person, which did not exist: the most common real-world failure
//     here is the right organization reached by someone who cannot attest, and the only affordances
//     were Back and a checkbox they must not tick;
//   - and the PRIMARY BUTTON'S WORD tracks that same fork (CPA-WEB47, `expectationSubmitLabel`): a
//     mismatch or a domain-less organization reads "Next", because the block above it has just said a
//     reviewer will ask for a document and, where the claim does go to review, the claim-detail page
//     and its document upload are what comes next. "Submit claim" is kept for the fork that can
//     actually end here. (Not universal — a mismatch the backend verifies anyway ends on a terminal
//     panel in place under a button that said "Next"; the under-promise is the safe direction, see
//     `expectationSubmitLabel`.) The ceremony's gate is untouched.
//
// ★ EMPTY-NAME GUARD. The legal sentence names the organization, so it must never render with an empty
// subject. `!charity` alone does NOT catch that: when the singleton read answers off-contract with a
// `{data,meta,links}` envelope, the api-client's envelope middleware unwraps it to a bare `[]` — which
// is truthy, has no `.name`, and would have produced "I am an authorized representative of , and the
// information I provide is accurate." The guard is on the resolved NAME, and it fails to the error
// state rather than showing an attestation nobody can read.
//
// ★ THE BUNDLED CEREMONY (CPA-WEB12, D-CP-37) — the wizard's only arm since CPA-WEB87 retired the build
// knob that chose between it and the attestation-only screen (`lib/env.ts`). This screen is the one act
// that ends in a recorded §318(a) consent: the Charity Terms are read in flow, the claimant states a
// (f)(1) role and a title, and TWO affirmations are made — authority (about the person) and consent (by
// the organization). The same POST carries four more fields: `terms_version_number`,
// `terms_content_hash`, `signer_role`, `signer_title`. Composition lives in `claim-signing-block.tsx`;
// the ceremony's rails are documented there.
//
//   ⚠ ECHO WHAT YOU **CONSENTED TO**, WHICH IS MORE THAN ECHOING WHAT IS ON SCREEN. `submit` reads
//   `termsQuery.data` — the object the render the claimant is looking at was built from — and never
//   re-fetches. That alone is not enough: the document can change between the tick and the click, with
//   no submit and no 409 (a background refetch — see the effect below), and then "what is on screen" is
//   no longer "what was agreed to". Both routes to that swap now end in the same place: the consent
//   affirmation is cleared, the rotation notice is raised, and the claimant re-consents against the body
//   now shown. Never retry the POST — that is how a consent is recorded against a document nobody read.
//
// The root keeps `data-testid="page-claim-wizard"`. On AUTO_VERIFIED the success panel is
// `claim-wizard-auto-verified`; on CANNOT_CLAIM the dead-end is `claim-wizard-cannot-claim`.
//
// ★ CONTINUE WAITS FOR THE NEW MEMBERSHIP (CPA-WEB83). The AUTO_VERIFIED panel paints from the cache
// BEFORE the memberships refetch lands (CPA-WEB51's ordering, kept: no form flash, no re-submit
// window), so its Continue used to be live while the cached pre-claim `[]` still read `loaded-empty`.
// A quick click went to the root, the portal-entry gate redirected that "no organization yet" caller
// to `/claim`, and when the refetch landed the shell rendered there — a claim-first path that never
// moves a member — leaving the new Owner on the org search (the onboarding rehearsal's red; a slow
// network strands a person the same way). Continue now holds (`loading`) while that read is in flight
// without the charity, and once the list holds it, opens the organization deep link (CPA-BE29's door,
// `router/organization-deep-link.tsx`), which selects THIS organization through the one writer and
// lands on its console's landing — the right console for an Owner who administers others too. A read
// that settles without the charity (an error) releases it to the doorway, as before, whose own error
// state is the way back. The mechanism is `org/continue-into-organization.ts`, shared with the claim
// page's approved card; pinned through the real route tree in `router/claim-wizard-terminal.test.tsx`.
//
// ★ THE ROUTE MOUNTS THIS PAGE THROUGH ITS ENTRY, NOT DIRECTLY (`claim-wizard-entry.tsx`, CPA-WEB52 +
// CPA-WEB53). Two doors stand in front of the ceremony: a caller who ALREADY administers this charity
// is entered into their console instead of being asked to claim it again, and a signed-in caller
// whose address is a personal-email domain is refused with the same message the sign-up form gives.
// A cached terminal result (above) outranks both — a claimant who just submitted sees the panel. This
// page stays the ceremony alone, which is what its two unit suites render bare; the doors are pinned
// through the real route tree (`router/claim-wizard-entry.test.tsx`).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  charitiesGetCharityOptions,
  charityAdminListMyCharityClaimsOptions,
  charityAdminListMyMembershipsOptions,
} from '@givmo/api-client/query';
import {
  sdk,
  isGivmoError,
  type CharityOut,
  type CharityClaimSubmitResultOut,
  type CharityTermsOut,
} from '@givmo/api-client';
import {
  Avatar,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorMessage,
  Icon,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Copy,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../../auth/auth-context';
import { useContinueIntoOrganization } from '../../org/continue-into-organization';
import { classifyTermsAcceptanceConflict } from '../terms/terms-acceptance-conflicts';
import { formatEin } from './claim-decode';
import { decodeClaimTerms, termsVersionLabel } from './claim-terms-decode';
import { claimTermsKey, getClaimTerms } from './claim-terms-transport';
import { claimTerminalResultKey, type ClaimTerminalResult } from './claim-terminal-result';
import {
  ClaimSigningBlock,
  EMPTY_SIGNING_STATE,
  signingSubmission,
  type SigningState,
} from './claim-signing-block';
import { CLAIM_OUTCOME, CLAIM_REVIEW_SLA_COPY } from './claim-vocab';
import {
  claimExpectation,
  expectationSentences,
  expectationSubmitLabel,
} from './claim-expectation';
import { CLAIM_ROUTES, claimDetailPath } from './claim-routes';
import { FUNNEL_STEP_CONFIRM, FUNNEL_TOTAL_STEPS } from '../../lib/funnel-copy';

const PREFIX = 'claim-wizard';

export function ClaimWizardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // The wizard is mounted under the pathless `authenticated` layout, so its registered route id is
  // `/authenticated/claim/$charityId` (TanStack prefixes the layout id).
  const { charityId } = useParams({ from: '/authenticated/claim/$charityId' });

  const charityQuery = useQuery(charitiesGetCharityOptions({ path: { charity_id: charityId } }));
  const charity: CharityOut | undefined = charityQuery.data;

  // The agreement this claimant will accept, read from the pre-approval route.
  //
  // `select` is where the tolerant decode lives, and it FAILS CLOSED: `null` means the bytes that
  // arrived cannot pin an acceptance, and the screen must say so rather than offer a submit whose echo
  // would be guessed. See `claim-terms-decode.ts`.
  const termsQuery = useQuery({
    queryKey: claimTermsKey(charityId),
    queryFn: () => getClaimTerms(charityId),
    select: decodeClaimTerms,
  });
  const terms = termsQuery.data ?? null;

  const [signing, setSigning] = useState<SigningState>(EMPTY_SIGNING_STATE);
  const [forwardCopied, setForwardCopied] = useState(false);
  // On AUTO_VERIFIED / CANNOT_CLAIM we swap the form for a terminal panel in place
  // (PENDING_REVIEW navigates away to the claim-detail page instead).
  //
  // ★ THE TERMINAL RESULT LIVES IN THE QUERY CACHE, NOT ONLY HERE (CPA-WEB51). An approved claim
  // invalidates the memberships read, the refetch carries the new membership, and `PortalEntryGate`
  // swaps the onboarding chrome for the portal shell — a REMOUNT of this page, which would discard a
  // result held only in this instance's state and render the form again (measured: the success panel
  // never painted in the real app). The cache carries the answer across the remount: the mutation
  // writes it BEFORE the awaited invalidation, and a fresh instance reads it here. The local copy
  // still serves the paths that never remount (CANNOT_CLAIM, the request-less PENDING_REVIEW) and
  // the same-instance case when no swap happens. `skipToken`: nothing ever FETCHES this key — it is
  // written by the submit and read back, `staleTime: Infinity` because a submit that happened does
  // not go stale. Lifetime, scoping and the consent-integrity rule: `claim-terminal-result.ts`.
  const terminalQuery = useQuery({
    queryKey: claimTerminalResultKey(charityId),
    queryFn: skipToken,
    staleTime: Infinity,
  });
  const [terminalLocal, setTerminalLocal] = useState<ClaimTerminalResult | null>(null);
  const terminal: ClaimTerminalResult | null =
    (terminalQuery.data as ClaimTerminalResult | undefined) ?? terminalLocal;
  const result: CharityClaimSubmitResultOut | null = terminal?.result ?? null;
  // CPA-WEB83: Continue holds while the memberships read is in flight without this charity, then
  // opens its console (`org/continue-into-organization.ts`). No `refresh`: the submit's invalidation
  // below is what re-asks the read.
  const continueInto = useContinueIntoOrganization(charityId);
  // Which version the acceptance was recorded against — the label of the document the submit ECHOED,
  // captured inside the mutation from the same object whose version + hash rode the POST, and carried
  // in the cached result; `null` only for a submit that echoed no acceptance. Never re-derived from
  // the current Terms read: the remounted instance re-asks that read, and a document rotated between
  // the submit and the remount would otherwise be credited with an acceptance nobody gave. The
  // confirmation no longer prints the label (D-CP-84, CPA-WEB78) — only whether one exists.
  const acceptedVersion: string | null = terminal?.acceptedVersion ?? null;
  // B2: a failed submit used to exist only as a 4-second toast — the one failure on this screen, gone
  // before a slow reader noticed it, on the one action that matters. It now persists inline as well.
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The two refusals that are NOT ordinary errors: a rotated document (re-present it and require the
  // consent affirmation again) and an organization with no EIN on file (unreachable through a
  // well-formed catalog, and a data defect rather than a user error when it happens).
  const [termsRotated, setTermsRotated] = useState(false);
  const [missingEin, setMissingEin] = useState(false);

  // ★ THE DOCUMENT CAN CHANGE UNDER A CONSENT THAT WAS ALREADY GIVEN, WITHOUT ANY 409.
  //
  // The 409 path below handles the version rotating at SUBMIT. This handles it rotating while the
  // claimant is still on the screen, which needs no submit at all: `createQueryClient()` turns
  // `refetchOnWindowFocus` off but says nothing about `refetchOnReconnect`, so it takes TanStack's
  // default of `true` — and with `staleTime: 30_000`, a query is stale long before anybody finishes
  // reading a legal agreement. A phone that drops and regains its connection mid-read therefore
  // refetches this document in the background, and the affirmation carries no pin to what it was
  // given for. Without this the ticked box survives the swap, the gate stays open, and the submit
  // echoes a version and hash the claimant never saw — a recorded §318(a) consent to a document
  // nobody read, which is exactly what D-CP-8 exists to make impossible. Nothing downstream catches
  // it either: the echo IS current, so the backend's drift guard agrees with a consent that was
  // never given.
  //
  // ONE cure, not two: the event is the same event the 409 path names, so it gets the same
  // treatment — clear the consent affirmation (the authority affirmation is about the person and has
  // not become untrue), raise the SAME rotation notice, and let the claimant re-consent against the
  // body now on screen.
  const displayedHashRef = useRef<string | null>(null);
  const displayedHash = terms?.content_hash ?? null;
  const consentAffirmed = signing.termsAccepted;
  useEffect(() => {
    // Never pin `null`: the document momentarily having no readable value (a refetch the decode
    // refuses, a transport blip) must not erase the hash the consent was actually given for, or the
    // swap that follows would look like a first load and slip through.
    if (displayedHash === null) return;
    const previous = displayedHashRef.current;
    displayedHashRef.current = displayedHash;
    if (previous === null || previous === displayedHash) return;
    if (!consentAffirmed) return;
    setSigning((current) => ({ ...current, termsAccepted: false }));
    setTermsRotated(true);
  }, [displayedHash, consentAffirmed]);

  const submit = useMutation({
    mutationFn: async (): Promise<ClaimTerminalResult> => {
      // ⚠ The acceptance echo is read from the query data THIS RENDER displayed. Never re-fetch here.
      const signed = signingSubmission(signing, terms);
      const { data } = await sdk.charityAdminSubmitCharityClaim({
        path: { charity_id: charityId },
        body: {
          attested: true,
          // The name the affirmations displayed (CPA-BE82, `signingName` below), untrimmed as
          // served. The backend records the same name itself whatever arrives here.
          attested_legal_name: terms?.organization_name ?? charity?.name ?? null,
          attested_ein: charity?.EIN ?? null,
          ...(signed ?? {}),
        },
        throwOnError: true,
      });
      // The accepted version is named from the SAME object the echo above was built from — inside the
      // mutation, so a document that rotates while the POST is in flight (or before a remount) can
      // never relabel what was actually accepted (CPA-WEB51, consent integrity).
      return {
        result: data,
        acceptedVersion: signed !== null && terms !== null ? termsVersionLabel(terms) : null,
      };
    },
    onSuccess: async (submitted) => {
      const data = submitted.result;
      setSubmitError(null);
      // PENDING_REVIEW → hand off to the claim-detail page (Tier-2 docs + status). This is also the
      // takeover branch (an already-claimed org always routes here). We can only route when `request`
      // is present (it carries the claim id); a PENDING_REVIEW with a null/absent `request` (shouldn't
      // happen per the contract, but we don't crash on it) renders the terminal pending panel in place.
      //
      // ★ THE CLAIMS READ IS REFRESHED BEFORE THE HANDOFF (surfaced by CPA-UIX11, the first time the
      // handoff was looked at). The claim-detail page finds its claim in the LIST read the portal-entry
      // gate already made for this caller — `[]`, fetched before the submit and fresh for the app's
      // 30-second stale window — so it landed on "Claim not found" for the claim just recorded, until
      // a window focus happened to re-ask. Awaiting the invalidation here means the page mounts over
      // a list that carries the request (the backend answers it on the next read); the gate reads the
      // same list and moves to its claim-pending posture in the same step, with no chrome swap on a
      // claim-first path. A failed refetch resolves rather than throwing, so the handoff still happens
      // and the page's own retry is the way back.
      if (data.outcome === CLAIM_OUTCOME.pendingReview && data.request) {
        await queryClient.invalidateQueries({
          queryKey: charityAdminListMyCharityClaimsOptions().queryKey,
        });
        await navigate({ to: claimDetailPath(data.request.id) });
        return;
      }
      // AUTO_VERIFIED, CANNOT_CLAIM, and a request-less PENDING_REVIEW render a terminal panel in place.
      // ★ INTO THE CACHE FIRST, BEFORE THE AWAITED INVALIDATION BELOW (CPA-WEB51): the invalidation's
      // refetch is what swaps the chrome and REMOUNTS this page, and the state write at the end of this
      // handler would then land on a dead instance. The fresh instance reads this entry at its FIRST
      // paint, so the swap's own commit already shows the panel — no form flash, no re-submit window
      // (pinned by the first-paint snapshot in `router/claim-wizard-terminal.test.tsx`).
      queryClient.setQueryData(claimTerminalResultKey(charityId), submitted);
      if (data.outcome === CLAIM_OUTCOME.autoVerified) {
        await queryClient.invalidateQueries({
          queryKey: charityAdminListMyMembershipsOptions().queryKey,
        });
      }
      setTerminalLocal(submitted);
    },
    onError: async (error: unknown) => {
      // A 409 is a REFUSAL TO RECORD, not a transport failure, and the two kinds need opposite
      // responses. The classifier is shared with the served Terms page and follows the server's own
      // order of refusal (EIN before hash) — `terms-acceptance-conflicts.ts`.
      const conflict = classifyTermsAcceptanceConflict(error);
      if (conflict === 'missing-ein') {
        // LOUD, not apologetic, and no retry offered: reaching this means an organization is in the
        // catalog with no EIN, which is a data defect on our side. `Charity.EIN` is non-optional and
        // the catalog is IRS-derived, so it should be unreachable — a non-null column can still hold
        // an empty value. Nothing the claimant can do changes it, so the screen stops asking.
        setMissingEin(true);
        setSubmitError(null);
        return;
      }
      if (conflict !== null) {
        // The document rotated under the reader (or the backend refused the echo for any other
        // reason). Re-present the CURRENT body and clear the consent affirmation — the authority
        // affirmation is about the person and has not become untrue, so it stands. Never retry the
        // POST: that is how a consent gets recorded against a document nobody read.
        setSubmitError(null);
        setSigning((previous) => ({ ...previous, termsAccepted: false }));
        setTermsRotated(true);
        await termsQuery.refetch();
        return;
      }
      setSubmitError(
        isGivmoError(error) ? error.message : 'Could not submit your claim. Please try again.',
      );
    },
  });

  // ── Loading / error / not-found for the charity itself ────────────────────────────────────────
  if (charityQuery.isPending) {
    return (
      <WizardShell>
        <WizardLoadingCard />
      </WizardShell>
    );
  }

  const legalName = typeof charity?.name === 'string' ? charity.name.trim() : '';

  if (charityQuery.isError || !charity || legalName.length === 0) {
    return (
      <WizardShell>
        <EmptyState
          testId={`${PREFIX}-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn't load this organization"
          description={
            isGivmoError(charityQuery.error)
              ? charityQuery.error.message
              : 'We could not load this organization. Go back and try another search.'
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
              onClick={() => void navigate({ to: CLAIM_ROUTES.search })}
              testId={`${PREFIX}-back-to-search`}
            >
              Back to search
            </Button>
          }
        />
      </WizardShell>
    );
  }

  const einDisplay = formatEin(charity.EIN);
  // ★ THE AFFIRMATIONS NAME THE ORGANIZATION AS THE AGREEMENT DOES (CPA-BE82). `legalName` above is
  // `GET /charities/{id}`'s `name`, which is the organization's display name while one is live: right
  // for the screen around the ceremony, wrong inside a legal statement. The two affirmations and the
  // submit's `attested_legal_name` take the name the served Terms body prints, its catalog name. A
  // backend before CPA-BE82 serves none, and then the name read above stands, as it always has.
  const signingName = terms?.organization_name?.trim() || legalName;
  // While they differ, the organization card names it as donors see it and the affirmations as the
  // agreement does, so the signing block ties the two together in one line.
  const donorName = legalName && legalName !== signingName ? legalName : null;

  // The one line a claimant gets back for the legal act they just performed: THAT their organization
  // accepted the Charity Terms — not which version (D-CP-84, CPA-WEB78; Jonathan: the number is
  // record-keeping, the echo already pins the acceptance to the exact document). Rendered only where
  // the wizard itself announces the outcome — a Tier-2 claim navigates to the claim-detail page, which
  // has no acceptance surface yet (named in the CPA-WEB12 report as a follow-up, not silently skipped).
  // `null` when the submit echoed no acceptance.
  const acceptanceNote =
    acceptedVersion === null ? null : 'Your organization accepted the Charity Terms.';

  // ── Terminal: AUTO_VERIFIED ───────────────────────────────────────────────────────────────────
  if (result?.outcome === CLAIM_OUTCOME.autoVerified) {
    return (
      <WizardShell>
        <TerminalPanel
          testId={`${PREFIX}-auto-verified`}
          tone="success"
          icon={CheckCircle2}
          title={<>You&rsquo;re verified</>}
          body={
            result.message ||
            `Your claim for ${legalName} was verified instantly. You now manage this organization as its Global Admin.`
          }
          note={acceptanceNote}
          action={
            // Held while the post-claim memberships read is in flight without this charity; then
            // into THIS organization's console through its deep link (see the header, CPA-WEB83).
            <Button
              appearance="saas"
              tone="primary"
              leadingIcon={<Icon icon={LayoutDashboard} size={16} aria-hidden />}
              loading={continueInto.pending}
              onClick={continueInto.onContinue}
              testId={`${PREFIX}-go-dashboard`}
            >
              Continue
            </Button>
          }
        />
      </WizardShell>
    );
  }

  // ── Terminal: CANNOT_CLAIM (deactivated / screened dead-end) ──────────────────────────────────
  if (result?.outcome === CLAIM_OUTCOME.cannotClaim) {
    return (
      <WizardShell>
        <TerminalPanel
          testId={`${PREFIX}-cannot-claim`}
          tone="warning"
          icon={LifeBuoy}
          title={<>This organization can&rsquo;t be claimed</>}
          body={
            result.message ||
            'This organization is not currently eligible to be claimed. If you believe this is a mistake, please contact support.'
          }
          action={
            <div className="flex items-center gap-8">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void navigate({ to: CLAIM_ROUTES.search })}
                testId={`${PREFIX}-cannot-back`}
              >
                Back to search
              </Button>
              {/* EP-FIX7: the in-app `/support` page, not the 404ing `www.givmo.io/support`. Shown
                  to callers who may hold no membership, hence `/support`'s ClaimFirstGate exemption. */}
              <Link
                to="/support"
                className="inline-flex h-32 items-center rounded-md px-12 font-sans text-s13 font-w600 text-brand-700 outline-none transition-colors hover:bg-surface-hover focus-visible:shadow-ring motion-reduce:transition-none"
                {...testIdProp(`${PREFIX}-contact-support`)}
              >
                Contact support
              </Link>
            </div>
          }
        />
      </WizardShell>
    );
  }

  // ── Terminal: PENDING_REVIEW with NO request row (defensive fallback) ─────────────────────────
  // The happy PENDING_REVIEW path navigates to the claim-detail page using `request.id`. If the
  // response is PENDING_REVIEW but carries a null/absent `request` (off-contract), we have no claim id
  // to route to — so instead of silently dropping back to the ceremony (which reads as "nothing
  // happened" and invites a re-submit), we render an honest terminal panel confirming the claim is
  // under review. Mirrors the CANNOT_CLAIM dead-end shape.
  if (result?.outcome === CLAIM_OUTCOME.pendingReview) {
    return (
      <WizardShell>
        <TerminalPanel
          testId={`${PREFIX}-pending-review`}
          tone="info"
          icon={ShieldCheck}
          title="Your claim is being reviewed"
          body={
            result.message ||
            `Thanks — your claim for ${legalName} was submitted and a reviewer will confirm it, usually within ${CLAIM_REVIEW_SLA_COPY}. We'll email you when there's an update.`
          }
          note={acceptanceNote}
          action={
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              leadingIcon={<Icon icon={LayoutDashboard} size={14} aria-hidden />}
              onClick={() => void navigate({ to: CLAIM_ROUTES.claims })}
              testId={`${PREFIX}-pending-view-claims`}
            >
              View my claims
            </Button>
          }
        />
      </WizardShell>
    );
  }

  // ── The ceremony (default) ────────────────────────────────────────────────────────────────────
  const expectation = claimExpectation(user?.email, charity.organization_domain);
  const { lead, detail } = expectationSentences(expectation);

  return (
    <WizardShell>
      <Card testId={`${PREFIX}-org-summary`}>
        <CardBody className="flex flex-col gap-24 px-24 py-24">
          {/* The organization is the figure — this is the thing being claimed. */}
          <div className="flex items-center gap-16">
            <Avatar name={legalName} src={charity.logo_url ?? undefined} size="lg" />
            <div className="min-w-0">
              {/* WRAPS, never truncates (D-CP-56, the CPA-WEB43 ruling for the same defect on the
                  PageHeader chip). A real charity's legal name is routinely 65–77 characters, and
                  `truncate` cut the organization being claimed to "The Greater Metropolit…" while the
                  same name rendered in full three times further down the screen — at 1440 as well as at
                  375. `wrap-anywhere`, not `break-words`: only the former reduces an unbroken token's
                  min-content contribution, which is what actually contains a name with no spaces. */}
              <h2 className="wrap-anywhere font-sans text-s20 font-w600 leading-h13 tracking-givmo text-text-strong">
                {legalName}
              </h2>
              <p className="mt-4 flex flex-wrap items-center gap-8 font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                {einDisplay ? (
                  <span>
                    EIN <span className="tabular-nums">{einDisplay}</span>
                  </span>
                ) : null}
                {charity.organization_domain ? (
                  <span>
                    <span className="text-text-tertiary">Website</span>{' '}
                    {charity.organization_domain}
                  </span>
                ) : null}
              </p>
            </div>
          </div>

          {/* No attestation row here: the signing block's authority affirmation below the agreement
              REPLACED it (CPA-WEB12), saying the same thing in Jonathan's words beside the consent
              affirmation it is distinct from. Rendering both would ask one person to affirm their
              authority twice. */}

          {/* What to expect, personally and before committing. */}
          <div
            className="flex items-start gap-12 rounded-lg bg-surface-sunken p-16"
            {...testIdProp(`${PREFIX}-expectation`)}
          >
            <Icon
              icon={ShieldCheck}
              size={18}
              className="mt-2 shrink-0 text-brand-700"
              aria-hidden
            />
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              <span className="font-w600 text-text-strong">{lead}</span> {detail}
            </p>
          </div>

          {/* The exit that did not exist. No backend: a mail draft and a copied link, nothing more. */}
          <ForwardAffordance
            legalName={legalName}
            copied={forwardCopied}
            onCopied={setForwardCopied}
          />
        </CardBody>
      </Card>

      {/* ── The agreement, then the ceremony ────────────────────────────────────────────────────── */}
      <TermsSlot
        prefix={PREFIX}
        legalName={signingName}
        donorName={donorName}
        einDisplay={einDisplay}
        terms={terms}
        isPending={termsQuery.isPending || termsQuery.isFetching}
        isUnavailable={!termsQuery.isPending && !termsQuery.isFetching && terms === null}
        error={termsQuery.error}
        onRetry={() => void termsQuery.refetch()}
        rotated={termsRotated}
        state={signing}
        onChange={(next) => {
          setTermsRotated(false);
          setSigning(next);
        }}
        disabled={submit.isPending || missingEin}
      />

      {missingEin && (
        <div
          className="rounded-xl border border-danger-100 bg-danger-50 px-20 py-16"
          role="alert"
          {...testIdProp(`${PREFIX}-missing-ein`)}
        >
          <p className="font-sans text-s14 font-w700 tracking-givmo text-danger-700">
            We can&rsquo;t record this acceptance — {legalName} has no EIN on our records.
          </p>
          <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-danger-700">
            That is a gap in our records, not something you did wrong. Contact support and we will
            add this organization&rsquo;s EIN so you can finish claiming it.
          </p>
          <Link
            to="/support"
            className="mt-8 inline-flex items-center font-sans text-s13 font-w600 text-danger-700 underline underline-offset-2"
            {...testIdProp(`${PREFIX}-missing-ein-support`)}
          >
            Contact support
          </Link>
        </div>
      )}

      {submitError !== null && (
        <ErrorMessage testId={`${PREFIX}-submit-error`}>{submitError}</ErrorMessage>
      )}

      <div className="flex items-center justify-between">
        <Button
          appearance="saas"
          tone="ghost"
          size="sm"
          leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
          onClick={() => void navigate({ to: CLAIM_ROUTES.search })}
          testId={`${PREFIX}-back`}
        >
          Back
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          /* ONE gate, and it is the payload builder itself (`signingSubmission`) — so the button can
             never be live for a submit whose body the screen could not fill, and never dead for one it
             could. Every condition it checks is a backend requirement, not a courtesy. A recorded no-EIN
             refusal closes it for good: re-submitting cannot change the answer. */
          disabled={missingEin || signingSubmission(signing, terms) === null}
          loading={submit.isPending}
          onClick={() => {
            setSubmitError(null);
            submit.mutate();
          }}
          testId={`${PREFIX}-submit`}
        >
          {/* The WORD tracks the fork the expectation block just disclosed — "Next" where a reviewer
              and a document upload come after this, "Submit claim" where the claim can end here
              (CPA-WEB47). The gate above it is unchanged. */}
          {expectationSubmitLabel(expectation.kind)}
        </Button>
      </div>
    </WizardShell>
  );
}

/**
 * The agreement slot, and its four states.
 *
 * It exists as its own component because the FAILURE states are the interesting ones and they must not
 * be reachable as a live submit. The screen either has a document whose version and hash it can echo, or
 * it has no ceremony to offer — there is deliberately no middle state where the affirmations render
 * against a document the reader could not see.
 *
 *   loading      → a skeleton the size of the window, so the page does not jump when the agreement lands.
 *   no version   → the read 404'd: there is no effective Charity Terms version for this organization.
 *                  A defect on our side, so it fails loudly, names itself as ours, routes to support,
 *                  and offers NO retry — the same disposition as the no-EIN refusal, for the same
 *                  reason (a retry here can only ever produce the same refusal).
 *   unavailable  → the read failed some other way, or the decode refused what arrived (it fails
 *                  closed — see `claim-terms-decode.ts`). A retry, our own copy, and no affirmations:
 *                  the gate is already shut because `signingSubmission` needs the document, and this
 *                  says why out loud rather than leaving a disabled button unexplained.
 *   rotated      → the acceptance was refused because the document changed under the reader. The NEW
 *                  body is on screen and the consent affirmation was cleared, so the notice has to say
 *                  that the previous one no longer applies — copy carried from the served Terms page,
 *                  which solved the same problem first.
 *   ready        → the document + the ceremony (`claim-signing-block.tsx`).
 */
function TermsSlot({
  prefix,
  legalName,
  donorName,
  einDisplay,
  terms,
  isPending,
  isUnavailable,
  error,
  onRetry,
  rotated,
  state,
  onChange,
  disabled,
}: {
  prefix: string;
  legalName: string;
  donorName: string | null;
  einDisplay: string;
  terms: CharityTermsOut | null;
  isPending: boolean;
  isUnavailable: boolean;
  error: unknown;
  onRetry: () => void;
  rotated: boolean;
  state: SigningState;
  onChange: (next: SigningState) => void;
  disabled: boolean;
}) {
  if (isPending) {
    return (
      <Card className="px-24 py-24" testId={`${prefix}-terms-loading`}>
        <div className="flex flex-col gap-12">
          <Skeleton className="h-20 w-1/3" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-5/6" />
          <Skeleton className="h-12 w-4/6" />
        </div>
      </Card>
    );
  }

  if (isUnavailable || terms === null) {
    // A 404 is the backend saying there is no effective Charity Terms version to serve for this
    // organization at all (`_CLAIM_TERMS_READ_RESPONSES`, `charity_admin.py`). Nothing the claimant
    // does changes that, so it gets the no-EIN treatment — named as OURS, routed to support, and NO
    // retry — rather than a "Try again" whose only possible outcome is the same refusal forever.
    // That is the same reasoning the no-EIN refusal below already runs on, and it belongs here too.
    // (The map's 400 — no such charity — is equally terminal, but the catalog read that opened this
    // screen already resolved the charity, so reaching it means the two disagree; it stays on the
    // retryable branch rather than getting a third panel written for a state we have never seen.)
    if (isGivmoError(error) && error.status === 404) {
      return (
        <div
          className="rounded-xl border border-danger-100 bg-danger-50 px-20 py-16"
          role="alert"
          {...testIdProp(`${prefix}-terms-missing-version`)}
        >
          <p className="font-sans text-s14 font-w700 tracking-givmo text-danger-700">
            We can&rsquo;t show you the Charity Terms to accept &mdash; there is no current version
            on our side.
          </p>
          <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-danger-700">
            That is a gap in our records, not something you did wrong. Contact support and we will
            sort it out so you can finish claiming this organization.
          </p>
          <Link
            to="/support"
            className="mt-8 inline-flex items-center font-sans text-s13 font-w600 text-danger-700 underline underline-offset-2"
            {...testIdProp(`${prefix}-terms-missing-version-support`)}
          >
            Contact support
          </Link>
        </div>
      );
    }
    return (
      <EmptyState
        testId={`${prefix}-terms-unavailable`}
        icon={<Icon icon={FileText} size={20} aria-hidden />}
        title="Couldn&rsquo;t load the Charity Terms"
        /* OUR words, never the server's. The backend's detail strings are written for an API
           consumer, and this is the one sentence a nonprofit reads when the screen it was asked to
           sign something on fails. Nobody wrote those strings for this reader. */
        description="We could not load the agreement you need to accept to claim this organization. Please try again."
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onRetry}
            testId={`${prefix}-terms-retry`}
          >
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <>
      {rotated ? <RotationNotice prefix={prefix} /> : null}
      <ClaimSigningBlock
        prefix={prefix}
        legalName={legalName}
        donorName={donorName}
        einDisplay={einDisplay}
        terms={terms}
        state={state}
        onChange={onChange}
        disabled={disabled}
      />
    </>
  );
}

/**
 * The rotation notice, and the one thing about it that is not cosmetic: IT SCROLLS ITSELF INTO VIEW.
 *
 * The button that produced this refusal is at the BOTTOM of the page and the agreement it refers to is
 * near the top, so without this a claimant presses the primary button, nothing visibly happens where
 * they are looking, and the honest reading available to them is that the button is broken. The notice
 * has to arrive where the eye already is.
 *
 * It mounts only while `rotated` is true and unmounts the moment the consent is re-affirmed, so the
 * effect fires exactly once per rotation. `scrollIntoView` is optional-called because jsdom does not
 * implement it, and no `behavior` is passed — an unrequested animation is the wrong thing to do to a
 * reader who has just been interrupted, and `prefers-reduced-motion` should not have to be consulted
 * for a jump that was never animated.
 */
function RotationNotice({ prefix }: { prefix: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'center' });
  }, []);
  return (
    <div
      ref={ref}
      className="rounded-xl border border-warning-100 bg-warning-50 px-20 py-16 text-warning-700"
      role="status"
      {...testIdProp(`${prefix}-terms-updated`)}
    >
      <p className="font-sans text-s15 font-w700 tracking-givmo">
        The terms have been updated &mdash; please review again
      </p>
      <p className="mt-4 text-s13">
        Your claim was not submitted. Review the version now shown and accept it to continue.
      </p>
    </div>
  );
}

/**
 * "Not the right person for this?" — a mail draft to whoever is, plus a copy-link fallback for anyone
 * who does not use a mail client in the browser. Deliberately client-only: routing a claim invitation
 * through the backend is a real feature with real abuse questions, and this is the honest 90% of it.
 */
function ForwardAffordance({
  legalName,
  copied,
  onCopied,
}: {
  legalName: string;
  copied: boolean;
  onCopied: (copied: boolean) => void;
}) {
  const pageUrl = typeof window === 'undefined' ? '' : window.location.href;
  const mailto =
    `mailto:?subject=${encodeURIComponent(`Claiming ${legalName} on Givmo`)}` +
    `&body=${encodeURIComponent(
      `Someone authorized to act for ${legalName} needs to finish this claim on Givmo.\n\n${pageUrl}\n`,
    )}`;

  const onCopy = useCallback(() => {
    if (pageUrl.length === 0) return;
    void navigator.clipboard
      ?.writeText(pageUrl)
      .then(() => onCopied(true))
      .catch(() => onCopied(false));
  }, [pageUrl, onCopied]);

  return (
    <div className="flex flex-wrap items-center gap-x-12 gap-y-8 border-t border-border-subtle pt-20">
      <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
        Not the right person to confirm this?
      </span>
      <a
        href={mailto}
        className="inline-flex items-center gap-6 font-sans text-s13 font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none"
        {...testIdProp(`${PREFIX}-forward-email`)}
      >
        <Icon icon={Mail} size={14} aria-hidden />
        Send this to a colleague
      </a>
      <button
        type="button"
        onClick={onCopy}
        className="inline-flex items-center gap-6 font-sans text-s13 font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none"
        {...testIdProp(`${PREFIX}-forward-copy`)}
      >
        <Icon icon={Copy} size={14} aria-hidden />
        Copy link
      </button>
      <span role="status" className="font-sans text-s13 font-w400 tracking-givmo text-success-700">
        {copied ? 'Link copied' : ''}
      </span>
    </div>
  );
}

/**
 * The loading card the wizard shows while the organization resolves — and the frame the wizard's
 * ENTRY (`claim-wizard-entry.tsx`) shows while its two doors decide, so a member being forwarded to
 * their console and a caller whose address is being checked see the same quiet card, never a form.
 * M4: the same 44px identity block + two metadata lines the loaded screen renders (Avatar `lg` is
 * 44px), so the organization does not jump into a different shape when it lands.
 */
export function WizardLoadingCard() {
  return (
    <Card className="px-24 py-24" testId={`${PREFIX}-loading`}>
      <div className="flex items-center gap-16">
        <Skeleton className="size-44 shrink-0 rounded-full" />
        <div className="flex flex-1 flex-col gap-8">
          <Skeleton className="h-20 w-1/2" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      </div>
      <Skeleton className="mt-24 h-[92px] w-full rounded-xl" />
    </Card>
  );
}

/**
 * The three terminal outcomes share one composition — only the tone, glyph and copy differ. Exported
 * for the entry's personal-email refusal (CPA-WEB53), which is a fourth dead-end of the same shape.
 */
export function TerminalPanel({
  testId,
  tone,
  icon,
  title,
  body,
  note,
  action,
}: {
  testId: string;
  tone: 'success' | 'warning' | 'info';
  icon: ComponentProps<typeof Icon>['icon'];
  title: ReactNode;
  body: string;
  /** A second, quieter line under the body. Used to say the organization accepted the Charity Terms. */
  note?: string | null;
  action: ReactNode;
}) {
  // M3: each glyph sits at its own -700 step over the matching -50 tint, so all three read at the
  // same weight instead of the warning and info marks reading a step lighter than the success one.
  const tones = {
    success: 'bg-success-50 text-success-700',
    warning: 'bg-warning-50 text-warning-700',
    info: 'bg-info-50 text-info-700',
  } as const;
  return (
    <Card className="overflow-hidden" testId={testId}>
      <CardBody className="flex flex-col items-center gap-16 px-24 py-32 text-center">
        <span
          className={`flex size-[56px] items-center justify-center rounded-full ${tones[tone]}`}
        >
          <Icon icon={icon} size={28} aria-hidden />
        </span>
        <div className="flex flex-col gap-6">
          {/* M1: `text-h5` was never a token — these headings were rendering at body size. */}
          <h2 className="font-sans text-s20 font-w600 tracking-givmo text-text-strong">{title}</h2>
          <p className="max-w-[460px] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {body}
          </p>
          {note ? (
            <p
              className="max-w-[460px] font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-tertiary"
              {...testIdProp(`${testId}-acceptance`)}
            >
              {note}
            </p>
          ) : null}
        </div>
        {action}
      </CardBody>
    </Card>
  );
}

/**
 * Shared page frame for the wizard (step cue + header + max-width column). Exported so the entry's
 * doors (`claim-wizard-entry.tsx`) render inside the same frame the ceremony wears.
 *
 * `stepCue={false}` drops the step cue and the heading for a screen that is NOT a step of the
 * ceremony: the entry's personal-email refusal (CPA-WEB53) happens before any step, and "Step 2 of
 * 3 · Confirm you're authorized" over "we can't continue" is a contradiction. The loading card and
 * every post-submit outcome keep the frame — those are answers to step 2. The root testid
 * (`page-claim-wizard`) and the column are the same either way.
 */
export function WizardShell({
  children,
  stepCue = true,
}: {
  children: ReactNode;
  stepCue?: boolean;
}) {
  return (
    <div className="flex w-full justify-center px-24 py-44" {...testIdProp('page-claim-wizard')}>
      <div className="flex w-full max-w-[680px] flex-col gap-20">
        {stepCue ? (
          <div className="flex flex-col gap-6">
            <p
              className="font-sans text-s12 font-w600 uppercase tracking-[0.06em] text-text-secondary"
              {...testIdProp('claim-wizard-step-cue')}
            >
              Step <span className="tabular-nums">{FUNNEL_STEP_CONFIRM.n}</span> of{' '}
              <span className="tabular-nums">{FUNNEL_TOTAL_STEPS}</span> ·{' '}
              {FUNNEL_STEP_CONFIRM.title}
            </p>
            <h1 className="font-sans text-s24 font-w600 leading-h13 tracking-givmo text-text-strong">
              Confirm you can act for this organization
            </h1>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}
